import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  useCreateDeductionEmployee,
  useUpdateDeductionEmployee,
} from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetDeductionEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { Deduction } from '@nxsys/store/slices/paymentSetting/types';
import {
  CreateDeductionEmployeeParams,
  UpdateDeductionEmployeeParams,
} from '@nxsys/types/api';
import { UMBRELLA_PAYE_HYBRID } from '@nxsys/constants/AppConst';
import { isCISPaymentModel } from '@nxsys/helpers/Common';
import { Icons } from '@nxsys/types/icons';
import { showToast } from '@nxsys/utils/toast';
import { Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { DeductionFormValues } from './DeductionForm';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CreateDeductionModal from './CreateDeductionModal';
import EditDeductionModal from './EditDeductionModal';
import { debounce, toNumber } from 'lodash';
import moment from 'moment';
import { formatMoney } from '@nxsys/utils/number-helper';
import { showCurrencyByCountry } from '@nxsys/utils';
import { defaultRepetitionOptions } from '@nxsys/constants/AppConstants';


type Props = {
  employeeId: string;
  paymentModel?: string;
};

const DeductionComponent: React.FC<Props> = ({ employeeId, paymentModel }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { isBureau, payrollCountry, isEmployee } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const isCISWorker = isCISPaymentModel(paymentModel);
  const isUmbrellaPAYE = UMBRELLA_PAYE_HYBRID.includes(paymentModel ?? '');

  const { deductionList } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { list, pagination, loading, queryParams } = deductionList;

  const handleGetDeductions = (params: GetDeductionEmployeeListParams) => {
    const newParams = {
      ...params,
      id: employeeId,
    };
    dispatch(employeeActions.getDeductionEmployeeList(newParams));
  };

  const refetchData = () => {
    handleGetDeductions({ ...queryParams });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleGetDeductions({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleGetDeductions({
        ...queryParams,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleGetDeductions({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleGetDeductions({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  useEffect(() => {
    handleGetDeductions({ page: 0, size: 20 });
  }, []);

  const columns: ColumnsType<Deduction> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="table-actions flex items-center justify-start">
          <li onClick={() => toggleEditDeduction(record)}>
            <Tooltip title="Edit">
              <span>
                <Icons.IconEdit />
              </span>
            </Tooltip>
          </li>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'deductionCode',
      title: messages['paymentSetting.deduction.deductionCode'],
      dataIndex: 'deductionCode',
      fixed: 'left',
    },
    {
      key: 'description',
      title: messages['common.description'],
      dataIndex: 'description',
    },
    ...((isIreland
      ? [
          {
            key: 'repetition',
            title: messages['common.repetition'],
            dataIndex: 'repetition',
            width: 300,
            render: (value) =>
              defaultRepetitionOptions.find((option) => option.value === value)
                ?.label || '-',
          },
        ]
      : []) as ColumnsType<Deduction>),
    ...(!isBureau
      ? [
          {
            key: 'deductionType',
            title: messages['common.deductionType'],
            dataIndex: 'deductionType',
          },
        ]
      : ([] as ColumnsType<Deduction>)),
    {
      key: 'minDeductionPeriod',
      title: messages['paymentSetting.payment.minDeductionPeriod'],
      dataIndex: 'minDeductionPeriod',
      render: (value) =>
        value ? formatMoney(value, showCurrencyByCountry(payrollCountry)) : '-',
    },
    {
      key: 'startDate',
      title: messages['common.startDate'],
      dataIndex: 'startDate',
      render: (value) =>
        value ? moment(value, 'MM-DD-YYYY').format('DD/MM/YYYY') : '-',
    },
    {
      key: 'endDate',
      title: messages['common.endDate'],
      dataIndex: 'endDate',
      render: (value) =>
        value ? moment(value, 'MM-DD-YYYY').format('DD/MM/YYYY') : '-',
    },
    {
      key: 'maxRecurrence',
      title: messages['paymentSetting.payment.maxRecurrence'],
      dataIndex: 'maxRecurrence',
    },
    {
      key: 'totalDeduction',
      title: messages['common.totalDeduction'],
      dataIndex: 'totalDeduction',
      render: (value) =>
        value ? formatMoney(value, showCurrencyByCountry(payrollCountry)) : '-',
    },
    ...((!isIreland && isCISWorker
      ? [
          {
            key: 'includeInCisCalculation',
            title: messages['common.beforeCISTax'],
            dataIndex: 'includeInCisCalculation',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
        ]
      : []) as ColumnsType<Deduction>),
    ...((!isIreland && isUmbrellaPAYE
      ? [
          {
            key: 'beforeNI',
            title: messages['common.beforeNI'],
            dataIndex: 'beforeNI',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'beforeTax',
            title: messages['common.beforeTax'],
            dataIndex: 'beforeTax',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'beforePension',
            title: messages['common.beforePension'],
            dataIndex: 'beforePension',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
        ]
      : []) as ColumnsType<Deduction>),
    ...((isIreland
      ? [
          {
            key: 'beforeTax',
            title: messages['common.beforeTax'],
            dataIndex: 'beforeTax',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'employeePRSICanBeDeducted',
            title: messages['common.beforeEmployeePRSI'],
            dataIndex: 'employeePRSICanBeDeducted',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'employerPRSICanBeDeducted',
            title: messages['common.beforeEmployerPRSI'],
            dataIndex: 'employerPRSICanBeDeducted',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'uscCanBeDeducted',
            title: messages['common.beforeUSC'],
            dataIndex: 'uscCanBeDeducted',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'ascCanBeDeducted',
            title: messages['common.beforeASC'],
            dataIndex: 'ascCanBeDeducted',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'pensionSchemaDeductionCanBeDeducted',
            title: messages['common.beforePensionSchemeDeductions'],
            dataIndex: 'pensionSchemaDeductionCanBeDeducted',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
            width: 300,
          },
          {
            key: 'lptCanBeDeducted',
            title: messages['paymentSetting.payment.beforeLPT'],
            dataIndex: 'lptCanBeDeducted',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'includeInCisCalculation',
            title: messages['common.beforeRCT'],
            dataIndex: 'includeInCisCalculation',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
        ]
      : []) as ColumnsType<Deduction>),
  ];

  const [openCreateDeduction, setOpenCreateDeduction] = useState(false);
  const toggleCreateDeduction = () => {
    setOpenCreateDeduction((prev) => !prev);
  };
  const { run: createDeductionEmployee } = useCreateDeductionEmployee();
  const onCreatedDeduction = async (values: DeductionFormValues) => {
    let params: CreateDeductionEmployeeParams;
    if (isIreland) {
      params = {
        deductionCode: null,
        deductionType: values?.deductionType?.value || null,
        description: values.description ?? '',
        startDate: values.startDate?.format('MM-DD-YYYY') ?? null,
        endDate: values.endDate?.format('MM-DD-YYYY') ?? null,
        minDeductionPeriod: values.minDeductionPeriod
          ? toNumber(values.minDeductionPeriod)
          : null,
        maxRecurrence: values.maxRecurrence
          ? toNumber(values.maxRecurrence)
          : null,
        totalDeduction: values.totalDeduction || null,
        includeInCisCalculation: values.includeInCisCalculation,
        repetition: values.repetition?.value || null,
        beforeTax: values.beforeTax,
        // NX-5656: the Ireland calc reads taxCanBeDeducted, not beforeTax —
        // keep both in sync so the toggle actually reaches the calculation
        taxCanBeDeducted: values.beforeTax,
        employeePRSICanBeDeducted: values.employeePRSICanBeDeducted,
        employerPRSICanBeDeducted: values.employerPRSICanBeDeducted,
        uscCanBeDeducted: values.uscCanBeDeducted,
        ascCanBeDeducted: values.ascCanBeDeducted,
        pensionSchemaDeductionCanBeDeducted:
          values.pensionSchemaDeductionCanBeDeducted,
        lptCanBeDeducted: values.lptCanBeDeducted,
      };
    } else if (isCISWorker) {
      params = {
        deductionCode: null,
        deductionType: values?.deductionType?.value || null,
        description: values.description ?? '',
        startDate: values.startDate?.format('MM-DD-YYYY') ?? null,
        endDate: values.endDate?.format('MM-DD-YYYY') ?? null,
        minDeductionPeriod: values.minDeductionPeriod
          ? toNumber(values.minDeductionPeriod)
          : null,
        maxRecurrence: values.maxRecurrence
          ? toNumber(values.maxRecurrence)
          : null,
        totalDeduction: values.totalDeduction || null,
        includeInCisCalculation: values.includeInCisCalculation,
      };
    } else {
      params = {
        deductionCode: null,
        deductionType: values?.deductionType?.value || null,
        description: values.description ?? '',
        startDate: values.startDate?.format('MM-DD-YYYY') ?? null,
        endDate: values.endDate?.format('MM-DD-YYYY') ?? null,
        minDeductionPeriod: values.minDeductionPeriod
          ? toNumber(values.minDeductionPeriod)
          : null,
        maxRecurrence: values.maxRecurrence
          ? toNumber(values.maxRecurrence)
          : null,
        totalDeduction: values.totalDeduction || null,
        beforeTax: values.beforeTax,
        beforeNI: values.beforeNI,
        beforePension: values.beforePension,
      };
    }
    const res = await createDeductionEmployee(employeeId, params, isIreland);
    if (res.success) {
      showToast({
        type: 'success',
        message: messages['common.addSuccess'],
      });
      refetchData();
    }
    toggleCreateDeduction();
  };

  const [openEditDeduction, setOpenEditDeduction] = useState(false);
  const [deductionSelected, setDeductionSelected] = useState<Deduction | null>(
    null,
  );
  const toggleEditDeduction = (record?: Deduction) => {
    setOpenEditDeduction((prev) => !prev);
    setDeductionSelected(record || null);
  };
  const { run: updateDeductionEmployee } = useUpdateDeductionEmployee();
  const onUpdatedPayment = async (values: DeductionFormValues) => {
    let params: UpdateDeductionEmployeeParams;
    if (isIreland) {
      params = {
        deductionCode: deductionSelected?.deductionCode || null,
        deductionType: values?.deductionType?.value || null,
        description: values.description ?? '',
        startDate: values.startDate?.format('MM-DD-YYYY') ?? null,
        endDate: values.endDate?.format('MM-DD-YYYY') ?? null,
        minDeductionPeriod: values.minDeductionPeriod
          ? toNumber(values.minDeductionPeriod)
          : null,
        maxRecurrence: values.maxRecurrence
          ? toNumber(values.maxRecurrence)
          : null,
        totalDeduction: values.totalDeduction || null,
        includeInCisCalculation: values.includeInCisCalculation,
        repetition: values.repetition?.value || null,
        beforeTax: values.beforeTax,
        // NX-5656: the Ireland calc reads taxCanBeDeducted, not beforeTax —
        // keep both in sync so the toggle actually reaches the calculation
        taxCanBeDeducted: values.beforeTax,
        employeePRSICanBeDeducted: values.employeePRSICanBeDeducted,
        employerPRSICanBeDeducted: values.employerPRSICanBeDeducted,
        uscCanBeDeducted: values.uscCanBeDeducted,
        ascCanBeDeducted: values.ascCanBeDeducted,
        pensionSchemaDeductionCanBeDeducted:
          values.pensionSchemaDeductionCanBeDeducted,
        lptCanBeDeducted: values.lptCanBeDeducted,
      };
    } else if (isCISWorker) {
      params = {
        deductionCode: deductionSelected?.deductionCode || null,
        deductionType: values?.deductionType?.value || null,
        description: values.description ?? '',
        startDate: values.startDate?.format('MM-DD-YYYY') ?? null,
        endDate: values.endDate?.format('MM-DD-YYYY') ?? null,
        minDeductionPeriod: values.minDeductionPeriod
          ? toNumber(values.minDeductionPeriod)
          : null,
        maxRecurrence: values.maxRecurrence
          ? toNumber(values.maxRecurrence)
          : null,
        totalDeduction: values.totalDeduction || null,
        includeInCisCalculation: values.includeInCisCalculation,
      };
    } else {
      params = {
        deductionCode: deductionSelected?.deductionCode || null,
        deductionType: values?.deductionType?.value || null,
        description: values.description ?? '',
        startDate: values.startDate?.format('MM-DD-YYYY') ?? null,
        endDate: values.endDate?.format('MM-DD-YYYY') ?? null,
        minDeductionPeriod: values.minDeductionPeriod
          ? toNumber(values.minDeductionPeriod)
          : null,
        maxRecurrence: values.maxRecurrence
          ? toNumber(values.maxRecurrence)
          : null,
        totalDeduction: values.totalDeduction || null,
        beforeTax: values.beforeTax,
        beforeNI: values.beforeNI,
        beforePension: values.beforePension,
      };
    }
    const res = await updateDeductionEmployee(
      deductionSelected!.id,
      employeeId,
      params,
      isIreland,
    );
    if (res.success) {
      showToast({
        type: 'success',
        message: messages['common.updateSuccess'],
      });
      refetchData();
    }
    toggleEditDeduction();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-[16px]">
        <AppInputSearch onChange={onChangeSearch} />
        {!isEmployee && (
          <AppButton
            type="primary"
            name={messages['common.addNew']}
            leftIcon={<PlusOutlined />}
            onClick={toggleCreateDeduction}
          />
        )}
      </div>
      <AppsContent>
        <AppTableContainer
          data={list || []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: isIreland ? 4000 : 2000, y: 700 }}
          titleEmpty={messages['employee.payment.empty']}
          columns={columns}
          onChangeTable={onChangeTable}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
      <CreateDeductionModal
        isOpen={openCreateDeduction}
        toggle={toggleCreateDeduction}
        onSubmit={onCreatedDeduction}
        paymentModel={paymentModel}
      />
      <EditDeductionModal
        isOpen={openEditDeduction}
        paymentModel={paymentModel}
        toggle={toggleEditDeduction}
        data={deductionSelected}
        onSubmit={onUpdatedPayment}
      />
    </div>
  );
};

export default DeductionComponent;
